"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var kentico_cloud_core_1 = require("kentico-cloud-core");
var rxjs_1 = require("rxjs");
var operators_1 = require("rxjs/operators");
var mappers_1 = require("../mappers");
var BaseDeliveryQueryService = /** @class */ (function () {
    function BaseDeliveryQueryService(
    /**
     * Delivery client configuration
     */
    config, 
    /**
     * Http service for fetching data
     */
    httpService, 
    /**
     * Used for manipulating with rich text HTML (required for Node / Browser support)
     */
    richTextHtmlParser, 
    /**
     * Information about the SDK
     * This can contain information from both this & Node SDK for internal logging with 'SDKID' header
     */
    sdkInfo) {
        this.config = config;
        this.httpService = httpService;
        this.richTextHtmlParser = richTextHtmlParser;
        this.sdkInfo = sdkInfo;
        /**
      * Default number of retry attempts when user did not set any
      */
        this.defaultRetryAttempts = 3;
        /**
         * List of response codes that can be retried
         */
        this.useRetryForResponseCodes = [500];
        /**
         * Header name for SDK usage
         */
        this.sdkVersionHeader = 'X-KC-SDKID';
        /**
         * Default base Url to Kentico Delivery API
         */
        this.defaultBaseDeliveryApiUrl = 'https://deliver.kenticocloud.com';
        /**
         * Default preview url to Kentico Delivery API
         */
        this.defaultPreviewDeliveryApiUrl = 'https://preview-deliver.kenticocloud.com';
        /**
         * Name of the header used when 'wait for loading new content' feature is used
         */
        this.waitForLoadingNewContentHeader = 'X-KC-Wait-For-Loading-New-Content';
        if (!config) {
            throw Error("Invalid configuration has been provided");
        }
        this.responseMapper = new mappers_1.ResponseMapper(config, richTextHtmlParser);
    }
    /**
 * Gets url based on the action, query configuration and options (parameters)
 * @param action Action (= url part) that will be hit
 * @param queryConfig Query configuration
 * @param options Query options
 */
    BaseDeliveryQueryService.prototype.getUrl = function (action, queryConfig, options) {
        return kentico_cloud_core_1.urlHelper.addOptionsToUrl(this.getBaseUrl(queryConfig) + action, options);
    };
    /**
   * Gets proper set of headers for given request.
   * @param queryConfig Query configuration
   * @param customHeaders Custom headers
   */
    BaseDeliveryQueryService.prototype.getHeaders = function (queryConfig) {
        var headers = [];
        // add headers from global config
        if (this.config.globalHeaders && this.config.globalHeaders.length > 0) {
            headers = headers.concat(this.config.globalHeaders);
        }
        // add headers from query config
        if (queryConfig.customHeaders) {
            headers = headers.concat(queryConfig.customHeaders);
        }
        // add SDK Id header for monitoring SDK usage
        headers.push(this.getSdkIdHeader());
        if (this.isPreviewModeEnabled(queryConfig) &&
            this.isSecuredModeEnabled(queryConfig)) {
            throw Error("Preview & secured modes cannot be used at the same time.");
        }
        // add preview header is required
        if (this.isPreviewModeEnabled(queryConfig)) {
            headers.push(this.getAuthorizationHeader(this.config.previewApiKey));
        }
        // add secured mode header is required
        if (this.isSecuredModeEnabled(queryConfig)) {
            headers.push(this.getAuthorizationHeader(this.config.securedApiKey));
        }
        // add 'X-KC-Wait-For-Loading-New-Content' header if required
        if (queryConfig.waitForLoadingNewContent) {
            headers.push({
                header: this.waitForLoadingNewContentHeader,
                value: 'true'
            });
        }
        return headers;
    };
    /**
     * Http GET response
     * @param url Url of request
     * @param queryConfig Query config configuration
     */
    BaseDeliveryQueryService.prototype.getResponse = function (url, queryConfig) {
        if (!queryConfig) {
            queryConfig = {};
        }
        return this.httpService
            .get({
            url: url,
            mapError: function (error) { return kentico_cloud_core_1.mapCloudError(error); }
        }, {
            headers: this.getHeaders(queryConfig),
            maxRetryAttempts: this.getRetryAttempts(),
            useRetryForResponseCodes: this.useRetryForResponseCodes,
            logErrorToConsole: this.config.enableAdvancedLogging
        })
            .pipe(operators_1.catchError(function (error) {
            return rxjs_1.throwError(error.mappedError);
        }));
    };
    /**
   * Gets base URL of the request including the project Id
   * @param queryConfig Query configuration
   */
    BaseDeliveryQueryService.prototype.getBaseUrl = function (queryConfig) {
        return this.getDeliveryUrl(queryConfig) + '/' + this.config.projectId;
    };
    /**
     * Gets number of retry attempts used by queries
     */
    BaseDeliveryQueryService.prototype.getRetryAttempts = function () {
        // get the attempts
        var attempts;
        if (this.config.retryAttempts || this.config.retryAttempts === 0) {
            // use custom defined number of attempts
            attempts = this.config.retryAttempts;
        }
        else {
            // use default attempts
            attempts = this.defaultRetryAttempts;
        }
        return attempts;
    };
    /**
     * Indicates if current query should use preview mode
     * @param queryConfig Query configuration
     */
    BaseDeliveryQueryService.prototype.isPreviewModeEnabled = function (queryConfig) {
        if (queryConfig.usePreviewMode != null) {
            return queryConfig.usePreviewMode;
        }
        return this.config.enablePreviewMode === true;
    };
    /**
     * Indicates if current query should use secured mode
     * @param queryConfig Query configuration
     */
    BaseDeliveryQueryService.prototype.isSecuredModeEnabled = function (queryConfig) {
        if (queryConfig.useSecuredMode != null) {
            return queryConfig.useSecuredMode;
        }
        return this.config.enableSecuredMode === true;
    };
    /**
     * Gets preview or standard URL based on client and query configuration
     * @param queryConfig Query configuration
     */
    BaseDeliveryQueryService.prototype.getDeliveryUrl = function (queryConfig) {
        if (this.isPreviewModeEnabled(queryConfig)) {
            if (!this.config.previewApiKey) {
                throw Error("You have to configure 'previewApiKey' to use 'preview' mode");
            }
            // use custom base / preview url if its configured
            if (this.config.basePreviewUrl) {
                return this.config.basePreviewUrl;
            }
            // use default preview url
            return this.defaultPreviewDeliveryApiUrl;
        }
        // use custom base / preview url if its configured
        if (this.config.baseUrl) {
            return this.config.baseUrl;
        }
        return this.defaultBaseDeliveryApiUrl;
    };
    /**
     * Gets authorization header. This is used for 'preview' functionality
     */
    BaseDeliveryQueryService.prototype.getAuthorizationHeader = function (key) {
        if (!key) {
            throw Error("Cannot get authorization header because key is undefined");
        }
        // authorization header required for preview mode
        return {
            header: 'authorization',
            value: "bearer " + key
        };
    };
    /**
     * Header identifying SDK type & version for internal purposes of Kentico
     */
    BaseDeliveryQueryService.prototype.getSdkIdHeader = function () {
        return {
            header: this.sdkVersionHeader,
            value: this.sdkInfo.host + ";" + this.sdkInfo.name + ";" + this.sdkInfo.version
        };
    };
    return BaseDeliveryQueryService;
}());
exports.BaseDeliveryQueryService = BaseDeliveryQueryService;
//# sourceMappingURL=base-delivery-query.service.js.map